import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import CostcoChasePage from './components/CostcoChasePage';
import CostcoAuthCallback from './components/CostcoAuthCallback';
import CostcoNav from './components/CostcoNav';
import LoginPage from './components/LoginPage';
import SignupPage from './components/SignupPage';
import AccountPage from './components/AccountPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import { API_BASE_URL } from './App';
import './components/CostcoChasePage.css';

function CostcoAppContent() {
	const [authStatus, setAuthStatus] = useState(() => (Cookies.get('session') ? 'pending' : 'guest'));
	const [user, setUser] = useState(null);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const menuRef = useRef(null);
	const location = useLocation();
	const sessionId = Cookies.get('session');

	useEffect(() => {
		document.title = 'CostcoChase';
		const meta = document.querySelector('meta[name="description"]');
		const prevDescription = meta?.getAttribute('content');
		if (meta) meta.setAttribute('content', 'Track every Costco warehouse you visit — one lifetime log.');
		const publicUrl = process.env.PUBLIC_URL || '';
		const setLink = (rel, href, extra = {}) => {
			let el = document.querySelector(`link[rel="${rel}"]`);
			if (!el) {
				el = document.createElement('link');
				el.rel = rel;
				document.head.appendChild(el);
			}
			el.href = href;
			Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
		};
		setLink('icon', `${publicUrl}/assets/costco/favicon.svg`, { type: 'image/svg+xml' });
		setLink('apple-touch-icon', `${publicUrl}/assets/costco/apple-touch-icon.png`, { sizes: '180x180' });
		const style = document.createElement('style');
		style.id = 'costco-fonts';
		style.textContent = `
			@font-face {
				font-family: 'Costco Sans Web';
				font-style: normal;
				font-weight: 400;
				font-display: swap;
				src: url('${publicUrl}/assets/costco/fonts/CostcoSans-Upright.woff2') format('woff2');
			}
			@font-face {
				font-family: 'Costco Sans Web';
				font-style: italic;
				font-weight: 400;
				font-display: swap;
				src: url('${publicUrl}/assets/costco/fonts/CostcoSans-Italic.woff2') format('woff2');
			}
		`;
		document.head.appendChild(style);
		return () => {
			style.remove();
			if (meta && prevDescription) meta.setAttribute('content', prevDescription);
		};
	}, []);

	useEffect(() => {
		const close = () => setIsMenuOpen(false);
		document.addEventListener('click', close);
		return () => document.removeEventListener('click', close);
	}, []);

	useEffect(() => {
		setIsMenuOpen(false);
	}, [location.pathname]);

	const validateSession = useCallback(() => {
		if (!sessionId) {
			setAuthStatus('guest');
			setUser(null);
			return Promise.resolve();
		}
		setAuthStatus('pending');
		return fetch(`${API_BASE_URL}/validate-session`, {
			method: 'POST',
			body: sessionId,
		})
			.then((r) => r.json())
			.then((data) => {
				if (data.valid && data.user) {
					setUser(data.user);
					setAuthStatus('authenticated');
				} else {
					Cookies.remove('session');
					setUser(null);
					setAuthStatus('guest');
				}
			})
			.catch(() => setAuthStatus('guest'));
	}, [sessionId]);

	useEffect(() => {
		validateSession();
	}, [validateSession]);

	const handleLogout = () => {
		Cookies.remove('session');
		setUser(null);
		setAuthStatus('guest');
		window.location.href = '/';
	};

	const showNav = !['/auth/callback'].includes(location.pathname);

	return (
		<div className="costco-app">
			{showNav && (
				<CostcoNav
					user={authStatus === 'authenticated' ? user : null}
					menuRef={menuRef}
					isMenuOpen={isMenuOpen}
					setIsMenuOpen={setIsMenuOpen}
					onLogout={handleLogout}
				/>
			)}
			<main className="costco-app__main">
				<Routes>
					<Route path="/auth/callback" element={<CostcoAuthCallback />} />
					<Route path="/login" element={<LoginPage product="costco" />} />
					<Route path="/signup" element={<SignupPage product="costco" />} />
					<Route
						path="/account"
						element={
							<AccountPage
								user={user}
								setUser={setUser}
								onSettingsSaved={validateSession}
								authStatus={authStatus}
								product="costco"
							/>
						}
					/>
					<Route path="/privacy-policy" element={<PrivacyPolicy />} />
					<Route path="/terms-of-service" element={<TermsOfService />} />
					<Route path="/*" element={<CostcoChasePage user={authStatus === 'authenticated' ? user : null} />} />
				</Routes>
			</main>
		</div>
	);
}

function CostcoApp() {
	return (
		<Router>
			<CostcoAppContent />
		</Router>
	);
}

export default CostcoApp;
